import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../../hooks/useTranslation';
import {
  Grid,
  Typography,
  Checkbox,
  makeStyles,
  createStyles,
  Theme,
  Paper,
  Card,
  CardContent,
  FormHelperText,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';

import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';

import { WizardFormProps } from '../../utility/wizard/wizard.types';
import {
  DirectAccessHistoricalUsageFormState,
  DirectAccessHistoricalUsageModel,
  HistoricalUsageAuthType,
} from '../../../hooks/useDirectAccessHistoricalUsage';
import PhoneTextField from '../../phone-text-field';
import EmailTextField from '../../email-text-field';
import { convertValidationRules } from '../../../hooks/useFormState';
import {
  validateEmail,
  validateExistsAndAlphabetCharacters,
  validateNameField,
  validatePhoneNumber,
} from '../../../util/form-validation';
import { BaseValidationContext } from '../../../hooks/useFormState.types';
import clsx from 'clsx';
import TextField from '../../text-field';
import Backdrop from '../../backdrop';

import useAccountCustomer from '../../../hooks/useAccountCustomer';
import useAccountDetailList from '../../../hooks/useAccountDetailList';
import { getAccountsList } from '../../../hooks/useAccountMulticheckState';
import AccountListReducer from '../../account-summary/multi-accounts/accountListReducer';
import CircularProgress from '@material-ui/core/CircularProgress';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    marginTop0: {
      marginTop: theme.spacing(0),
    },
    marginTop1: {
      marginTop: '1em',
    },
    marginBottom1: {
      marginBottom: '1em',
    },
    errorMessage: {
      margin: '0 14px',
    },
    optionText: {
      paddingLeft: theme.spacing(4),
    },
    formField: {
      width: '100%',
      background: 'none',
      margin: 0,
    },
    cardContent: {
      padding: 16,
      marginBottom: 30,
    },
    divider: {
      marginTop: 16,
      marginBottom: 16,
    },
    note: {
      fontSize: '12px',
      marginTop: theme.spacing(2),
    },
    loadingContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing(1),
      padding: theme.spacing(2),
    },
    loadingText: {
      marginLeft: theme.spacing(1),
      color: theme.palette.text.secondary,
    },
    listItemText: {
      margin: theme.spacing(1),
    },
    checkboxItem: {
      alignItems: 'flex-start',
    },
    checkboxItemLabel: {
      '& p, & div': { margin: 0 },
    },

    accountNumber: {
      color: theme.palette.text.primary,
    },
    checkboxItemAllAccounts: {
      padding: '12px 50px 16px 22px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    customFlexCenter: {
      display: 'flex',
      alignItems: 'center',
    },
    checkboxLabel: {
      fontWeight: 700,
      marginLeft: theme.spacing(1),
    },
    totalAccountsText: {
      fontWeight: 400,
    },
    accordion: {
      backgroundColor: '#fff',
      borderRadius: 4,
      marginBottom: 16,
      borderLeft: '6px solid #27788C',
      border: 'none',
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.25)',
      transition: 'all 0.2s ease-in-out',

      '&:hover': {
        boxShadow: '0 4px 12px rgba(0,0,0,0.35)',
      },
      '&::before': {
        display: 'none',
      },
      '&$expanded': {
        margin: '16px 0',
      },
    },
    expanded: {},
    accountsFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      background: 'transparent',
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
    accordionSummary: {
      padding: '0 16px',
      minHeight: 64,
      borderBottom: '1px solid #E0E0E0',
      '&$expanded': {
        minHeight: 64,
      },
      '& .MuiAccordionSummary-content': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        margin: 0,
      },
    },
    accordionDetails: {
      background: '#fff',
      padding: '8px 16px',
      display: 'block',
    },
    accountList: {
      width: '100%',
      background: '#fff',
      overflow: 'hidden',
      textIndent: 24,
    },
    accountListItem: {
      borderBottom: 'none',
    },
  }),
);

export const DirectAccessHistoricalUsageForm = ({
  formState,
  componentProps,
}: WizardFormProps<
  DirectAccessHistoricalUsageModel,
  DirectAccessHistoricalUsageFormState
>) => {
  const { t, richT } = useTranslation();
  const {
    handleFormValidation,
    businesses,
    accountGroupsLoading,
  } = componentProps;

  const classes = useStyles();

  const [businessNameNotSelected, setBusinessNameNotSelected] = useState(false);
  const [expandedBusiness, setExpandedBusiness] = useState<string | null>(null);

  const [acknowledgementError, setAcknowledgementError] = useState(false);
  const [acknowledgements, setAcknowledgements] = useState([
    {
      id: 'ack01',
      label: t('HISTORICAL_USAGE_FORM_ACKNOWLEDGEMENTS_STATEMENT_01'),
      isSelected: false,
    },
    {
      id: 'ack02',
      label: richT('HISTORICAL_USAGE_FORM_ACKNOWLEDGEMENTS_STATEMENT_02'),
      isSelected: false,
    },
    {
      id: 'ack05',
      label: t('HISTORICAL_USAGE_FORM_ACKNOWLEDGEMENTS_STATEMENT_05'),
      isSelected: false,
    },
    {
      id: 'ack06',
      label: richT('HISTORICAL_USAGE_FORM_ACKNOWLEDGEMENTS_STATEMENT_06'),
      isSelected: false,
    },
    {
      id: 'ack07',
      label: t('HISTORICAL_USAGE_FORM_ACKNOWLEDGEMENTS_STATEMENT_07'),
      isSelected: false,
    },
  ]);

  const { groups } = useAccountCustomer();
  const accountsByGroupRef = useRef<Record<string, string[]>>({});
  const GroupAccounts: React.FC<{ groupId: string; pageSize?: number }> = ({
    groupId,
    pageSize = 5,
  }) => {
    const [page, setPage] = useState<number>(1);
    const { accounts, totalCount, loading } = useAccountDetailList(
      getAccountsList,
      groupId,
      {
        accountListParams: {
          ...AccountListReducer.initialState,
          pageSize,
          page,
        },
        skip: !groupId,
      },
      'cache-first',
    );

    const total = totalCount ?? (accounts ? accounts.length : 0);
    const totalPages = Math.max(1, Math.ceil((total || 0) / (pageSize || 1)));
    const handlePrev = () => setPage(p => Math.max(1, p - 1));
    const handleNext = () => setPage(p => Math.min(totalPages, p + 1));

    useEffect(() => {
      setPage(1);
    }, [groupId]);

    const { accounts: allAccounts } = useAccountDetailList(
      getAccountsList,
      groupId,
      {
        accountListParams: {
          ...AccountListReducer.initialState,
          pageSize: 9999,
          page: 1,
        },
        skip: !groupId,
      },
      'cache-first',
    );

    useEffect(() => {
      if (allAccounts && allAccounts.length > 0) {
        accountsByGroupRef.current[groupId] = allAccounts.map(
          a => a.accountNumber,
        );
      }
    }, [allAccounts, groupId]);

    if (loading) {
      return (
        <div className={classes.loadingContainer}>
          <CircularProgress size={24} />
          <Typography variant="body1" className={classes.loadingText}>
            Loading...
          </Typography>
        </div>
      );
    }
    if (!accounts || accounts.length === 0) {
      return (
        <Typography variant="body1" color="textSecondary">
          No associated accounts found.
        </Typography>
      );
    }

    return (
      <>
        <List className={classes.accountList} dense>
          {accounts.map(acc => (
            <ListItem
              key={acc.accountNumber}
              className={classes.accountListItem}
              disableGutters
              dense
            >
              <ListItemText
                className={classes.listItemText}
                primary={acc.accountNumber}
                primaryTypographyProps={{
                  className: classes.accountNumber,
                  component: 'span',
                  variant: 'body1',
                }}
              />
            </ListItem>
          ))}
        </List>

        <div className={classes.accountsFooter}>
          <Typography variant="body2">
            {t(
              'HISTORICAL_USAGE_FORM_BUSINESSES_LABEL_PAGINATION_SHOWING_PAGE',
            )}{' '}
            {page} {t('HISTORICAL_USAGE_FORM_BUSINESSES_LABEL_PAGINATION_OF')}{' '}
            {totalPages} ({total}{' '}
            {t('HISTORICAL_USAGE_FORM_BUSINESSES_LABEL_PAGINATION_ACCOUNTS')})
          </Typography>
          <div>
            <Button size="medium" onClick={handlePrev} disabled={page <= 1}>
              {t('HISTORICAL_USAGE_FORM_BUSINESSES_BUTTON_PREV')}
            </Button>
            <Button
              size="medium"
              onClick={handleNext}
              disabled={page >= totalPages}
            >
              {t('HISTORICAL_USAGE_FORM_BUSINESSES_BUTTON_NEXT')}
            </Button>
          </div>
        </div>
      </>
    );
  };

  useEffect(() => {
    const allAcknowledgementsChecked = acknowledgements.every(
      a => a.isSelected,
    );
    if (
      Object.keys(formState.errors).length === 0 &&
      businesses.some(
        (b: { name: string; isSelected: boolean; groupId: string }) =>
          b.isSelected,
      ) &&
      allAcknowledgementsChecked &&
      checkFieldValidations()
    ) {
      handleFormValidation(false);
    } else {
      handleFormValidation(true);
    }
  }, [businesses, formState.errors, formState.values]);

  useEffect(() => {
    if (formState.values.isSamePersonAuthorizing) {
      void formState.setValue('authFullName', '');
      void formState.setValue('authTitle', '');
      void formState.setValue('authEmail', '');
      void formState.setValue('authPhoneNumber', '');
    }
  }, [formState.values.isSamePersonAuthorizing]);

  const checkFieldValidations = () => {
    if (
      formState.values.isSamePersonAuthorizing &&
      (formState.values.companyName === '' ||
        formState.values.contactFullName === '' ||
        formState.values.contactTitle === '' ||
        formState.values.contactEmail === '' ||
        formState.values.contactPhoneNumber === '')
    ) {
      return false;
    } else if (
      !formState.values.isSamePersonAuthorizing &&
      Object.values(formState.values).some(o => o === '')
    ) {
      return false;
    } else {
      return true;
    }
  };

  const isAllAccountsSelected = businesses?.some(
    (b: any) =>
      (b.groupName === 'All Accounts' || b.groupId === groups?.[0]?.groupId) &&
      b.isSelected,
  );

  useEffect(() => {
    if (isAllAccountsSelected) {
      setExpandedBusiness(null);
    }
  }, [isAllAccountsSelected]);

  const handleBusinessSelection = (
    groupId: string,
    isSelected: boolean,
    numberOfAccounts?: number,
  ) => {
    const business: any = businesses.find(
      (b: { name: string; isSelected: boolean; groupId: string }) =>
        b.groupId === groupId,
    );
    if (business) {
      business.isSelected = isSelected;
      business.numberOfAccounts = numberOfAccounts;

      if (
        business.groupName === 'All Accounts' ||
        business.groupId === groups?.[0]?.groupId
      ) {
        const allAccounts: any[] = [];
        Object.values(accountsByGroupRef.current).forEach(groupAccounts => {
          allAccounts.push(...(groupAccounts || []));
        });
        business.accountsList = isSelected ? allAccounts : [];
      } else {
        business.accountsList = isSelected
          ? accountsByGroupRef.current[groupId] || []
          : [];
      }
      if (
        business.groupName === 'All Accounts' ||
        business.groupId === groups?.[0]?.groupId
      ) {
        businesses.forEach((b: any) => {
          if (b.groupId !== business.groupId) {
            b.disabled = isSelected;
            if (isSelected) {
              b.isSelected = false;
              b.accountsList = [];
            }
          }
        });
      }
    }
    void formState.setValue('businesses', businesses);
    if (
      !businesses.some(
        (b: { name: string; isSelected: boolean; groupId: string }) =>
          b.isSelected,
      )
    ) {
      setBusinessNameNotSelected(true);
    } else {
      setBusinessNameNotSelected(false);
    }
  };

  const handleAcknowledgementSelection = (id: string, isSelected: boolean) => {
    const ack = acknowledgements.find(a => a.id === id);
    if (ack) {
      ack.isSelected = isSelected;
    }

    const updatedAcks = [...acknowledgements];

    setAcknowledgements(updatedAcks);
    void formState.setValue('acknowledgements', updatedAcks);

    if (!updatedAcks.every(a => a.isSelected)) {
      setAcknowledgementError(true);
    } else {
      setAcknowledgementError(false);
    }
  };

  const handleAuthTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    void formState.setValue(
      'authType',
      event.target.value as HistoricalUsageAuthType,
    );
  };

  return (
    <>
      {accountGroupsLoading ? (
        <Backdrop forceOpen message={t('LOADING')} />
      ) : null}
      <Grid item xs={12} className={classes.marginTop1}>
        <Paper>
          <Card>
            <CardContent className={classes.cardContent}>
              {/* SUMMARY SECTION */}
              <Grid container direction="column">
                <Grid item container>
                  <Grid item xs={12}>
                    <Grid item className={classes.marginBottom1}>
                      <Typography
                        variant={'body1'}
                        className={classes.marginBottom1}
                      >
                        {richT('HISTORICAL_USAGE_FORM_SUMMARY')}
                      </Typography>

                      <Typography className={classes.note}>
                        {t('HISTORICAL_USAGE_FORM_SUMMARY_NOTE_FORM')}
                      </Typography>

                      <Typography className={classes.note}>
                        {t('HISTORICAL_USAGE_FORM_SUMMARY_NOTE')}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              {/* AUTHORIZATION SECTION */}
              <Divider className={classes.divider} />
              <Grid container direction="column">
                <Grid item container>
                  <Grid item xs={12}>
                    <Grid container direction="column" spacing={3}>
                      <Grid item>
                        <Typography variant="h2">
                          {t('HISTORICAL_USAGE_FORM_AUTHORIZATION_TITLE')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <RadioGroup
                          aria-label={'direct-access-authorization'}
                          data-testid={'authType'}
                          name={'authType'}
                          value={formState.values.authType}
                          onChange={handleAuthTypeChange}
                        >
                          <FormControlLabel
                            value={t('RELEASE')}
                            control={<Radio color={'primary'} />}
                            label={t(
                              'HISTORICAL_USAGE_FORM_AUTHORIZATION_RELEASE_OPTION',
                            )}
                          />
                          <Typography
                            variant={'body1'}
                            className={`${classes.marginBottom1} ${classes.optionText}`}
                          >
                            {t(
                              'HISTORICAL_USAGE_FORM_AUTHORIZATION_RELEASE_OPTION_TEXT',
                            )}
                          </Typography>

                          <FormControlLabel
                            value={t('REVOKE')}
                            control={<Radio color={'primary'} />}
                            label={t(
                              'HISTORICAL_USAGE_FORM_AUTHORIZATION_REVOKE_OPTION',
                            )}
                          />
                          <Typography
                            variant={'body1'}
                            className={classes.optionText}
                          >
                            {t(
                              'HISTORICAL_USAGE_FORM_AUTHORIZATION_REVOKE_OPTION_TEXT',
                            )}
                          </Typography>
                        </RadioGroup>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              {/* BUSINESS SECTION */}
              <Divider className={classes.divider} />
              <Grid container direction="column">
                <Grid item container>
                  <Grid item xs={12}>
                    <Typography variant="h2" className={classes.marginBottom1}>
                      {t('HISTORICAL_USAGE_FORM_BUSINESSES_TITLE')}
                    </Typography>
                    <Typography
                      variant={'body1'}
                      className={classes.marginBottom1}
                    >
                      {t('HISTORICAL_USAGE_FORM_BUSINESSES_TEXT')}
                    </Typography>
                    <Typography
                      variant={'body1'}
                      className={classes.marginBottom1}
                    >
                      {t('HISTORICAL_USAGE_FORM_BUSINESSES_NOTE')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="h2" className={classes.marginBottom1}>
                      {t('HISTORICAL_USAGE_FORM_BUSINESSES_LIST_TITLE')}
                    </Typography>

                    {(groups || []).map((group: any, index: number) => {
                      const isExpanded = expandedBusiness === group.groupId;
                      const businessState = businesses?.find(
                        (b: any) => b.groupId === group.groupId,
                      );
                      const isSelected = businessState?.isSelected || false;
                      const isAllAccounts =
                        index === 0 || group.groupName === 'All Accounts';

                      const isDisabled =
                        !isAllAccounts && isAllAccountsSelected;
                      const checkedState = isAllAccounts
                        ? isSelected
                        : isAllAccountsSelected || isSelected;

                      if (isAllAccounts) {
                        return (
                          <Grid
                            container
                            key={group.groupId || index}
                            className={classes.checkboxItemAllAccounts}
                            alignItems="center"
                            justify="space-between"
                          >
                            <Grid item>
                              <Grid
                                container
                                alignItems="center"
                                className={classes.customFlexCenter}
                              >
                                <Checkbox
                                  name={`business_${index}`}
                                  id={`business_${index}`}
                                  checked={isSelected}
                                  onChange={e =>
                                    handleBusinessSelection(
                                      group.groupId,
                                      e.target.checked,
                                      group.numberOfAccounts,
                                    )
                                  }
                                  color="primary"
                                />
                                <Typography
                                  variant="body1"
                                  className={classes.checkboxLabel}
                                >
                                  {group.groupName}
                                </Typography>
                              </Grid>
                            </Grid>

                            <Grid item>
                              <Typography
                                variant="body1"
                                className={classes.totalAccountsText}
                              >
                                {t(
                                  'HISTORICAL_USAGE_FORM_BUSINESSES_LABEL_TOTAL_ACCOUNTS',
                                )}
                                : {group.numberOfAccounts || 0}
                              </Typography>
                            </Grid>
                          </Grid>
                        );
                      }

                      return (
                        <Accordion
                          key={group.groupId || index}
                          expanded={isExpanded}
                          onChange={() =>
                            setExpandedBusiness(
                              isExpanded ? null : group.groupId,
                            )
                          }
                          className={classes.accordion}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            className={classes.accordionSummary}
                          >
                            <Grid
                              container
                              alignItems="center"
                              justify="space-between"
                            >
                              <Grid item className={classes.customFlexCenter}>
                                <Checkbox
                                  name={`business_${index}`}
                                  id={`business_${index}`}
                                  checked={isAllAccountsSelected || isSelected}
                                  disabled={isAllAccountsSelected}
                                  onChange={e =>
                                    handleBusinessSelection(
                                      group.groupId,
                                      e.target.checked,
                                      group.numberOfAccounts,
                                    )
                                  }
                                  color="primary"
                                />

                                <Typography
                                  variant="body1"
                                  className={classes.checkboxLabel}
                                >
                                  {group.groupName}
                                </Typography>
                              </Grid>
                              <Grid item>
                                <Typography
                                  variant="body1"
                                  className={classes.totalAccountsText}
                                >
                                  {t(
                                    'HISTORICAL_USAGE_FORM_BUSINESSES_LABEL_TOTAL_ACCOUNTS',
                                  )}
                                  : {group.numberOfAccounts || 0}
                                </Typography>
                              </Grid>
                            </Grid>
                          </AccordionSummary>
                          <AccordionDetails
                            className={classes.accordionDetails}
                          >
                            <Grid container spacing={1}>
                              <Grid item xs={12}>
                                <GroupAccounts groupId={group.groupId} />
                              </Grid>
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}

                    {businessNameNotSelected && (
                      <FormHelperText
                        className={clsx(
                          'MuiFormHelperText-root MuiFormHelperText-contained Mui-error',
                        )}
                      >
                        {t('SELECT_BUSINESS_NAME_ERROR')}
                      </FormHelperText>
                    )}

                    <Typography
                      variant={'body1'}
                      className={classes.marginTop1}
                    >
                      {t('HISTORICAL_USAGE_FORM_BUSINESSES_LIST_NOTE')}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              {/* ACKNOWLEDGEMENTS SECTION */}
              <Divider className={classes.divider} />
              <Grid container direction="column">
                <Grid item container>
                  <Grid item xs={12}>
                    <Typography variant="h2" className={classes.marginBottom1}>
                      {t('HISTORICAL_USAGE_FORM_ACKNOWLEDGEMENTS_TITLE')}
                    </Typography>
                    <Typography
                      variant="body1"
                      className={classes.marginBottom1}
                    >
                      {t('HISTORICAL_USAGE_FORM_ACKNOWLEDGEMENTS_INSTRUCTION')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    {acknowledgements.map((ack, index) => (
                      <Grid
                        key={ack.id}
                        container
                        direction="row"
                        className={classes.checkboxItem}
                        spacing={2}
                      >
                        <Checkbox
                          name={`ack_${index}`}
                          id={`ack_${index}`}
                          checked={ack.isSelected}
                          onChange={e =>
                            handleAcknowledgementSelection(
                              ack.id,
                              e.target.checked,
                            )
                          }
                          color="primary"
                        />

                        <Grid item xs>
                          <Typography
                            variant="body1"
                            className={classes.checkboxItemLabel}
                            component="span"
                          >
                            {ack.label}
                          </Typography>
                        </Grid>
                      </Grid>
                    ))}

                    {acknowledgementError && (
                      <FormHelperText
                        className={clsx(
                          'MuiFormHelperText-root MuiFormHelperText-contained Mui-error',
                        )}
                      >
                        {t('SELECT_ACKNOWLEDGEMENTS_ERROR')}
                      </FormHelperText>
                    )}

                    <Typography
                      variant={'body1'}
                      className={classes.marginTop1}
                    >
                      {richT(
                        'HISTORICAL_USAGE_FORM_ACKNOWLEDGEMENTS_CONTACT_SUPPORT',
                      )}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
              {/* AUTHORIZATION CONTACT SECTION */}
              <Divider className={classes.divider} />
              <Grid container direction="column" spacing={3}>
                <Grid item>
                  <Typography variant="h2">{t('AUTHORIZATION')}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name={'isSamePersonAuthorizing'}
                        id="sameAsAbove"
                        color={'primary'}
                        data-testid="isSamePersonAuthorizing"
                        checked={formState.values.isSamePersonAuthorizing}
                        {...formState.props('isSamePersonAuthorizing')}
                      />
                    }
                    // label={t('HISTORICAL_USAGE_CONTACT_SAME_AS_ABOVE')}
                    label={t('HISTORICAL_USAGE_CONTACT_SAME_AS_BELOW')}
                  />
                </Grid>
                {!formState.values.isSamePersonAuthorizing && (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        name={'authFullName'}
                        data-testid="authFullName"
                        label={t('CONTACT_INFO_FULL_NAME')}
                        inputProps={{
                          minLength: 1,
                          maxLength: 80,
                          'data-testid': 'test-authFullName',
                        }}
                        className={classes.formField}
                        {...formState.props('authFullName')}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name={'authTitle'}
                        data-testid="authTitle"
                        label={t('CONTACT_INFO_TITLE')}
                        inputProps={{
                          minLength: 1,
                          maxLength: 50,
                          'data-testid': 'test-authTitle',
                        }}
                        className={classes.formField}
                        {...formState.props('authTitle')}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <EmailTextField
                        margin="none"
                        name={'authEmail'}
                        label={t('CONTACT_INFO_EMAIL_ADDRESS')}
                        data-testid={'authEmail'}
                        {...formState.props('authEmail')}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <PhoneTextField
                        name={'authPhoneNumber'}
                        label={t('CONTACT_INFO_PHONE_NUMBER')}
                        className={classes.formField}
                        inputProps={{ minLength: 1, maxLength: 15 }}
                        data-testid="authPhoneNumber"
                        {...formState.props('authPhoneNumber')}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
              {/* CONTACT INFORMATION */}
              <Divider className={classes.divider} />
              <Grid container direction="column" spacing={3}>
                <Grid item>
                  <Typography variant="h2">
                    {t('CONTACT_INFORMATION')}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name={'companyName'}
                    data-testid="companyName"
                    label={t('CONTACT_INFO_COMPANY_NAME')}
                    inputProps={{
                      minLength: 1,
                      maxLength: 80,
                      'data-testid': 'test-companyName',
                    }}
                    className={classes.formField}
                    {...formState.props('companyName')}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name={'contactFullName'}
                    data-testid="contactFullName"
                    label={t('CONTACT_INFO_FULL_NAME')}
                    inputProps={{
                      minLength: 1,
                      maxLength: 80,
                      'data-testid': 'test-contactFullName',
                    }}
                    className={classes.formField}
                    {...formState.props('contactFullName')}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name={'contactTitle'}
                    data-testid="contactTitle"
                    label={t('CONTACT_INFO_TITLE')}
                    inputProps={{
                      minLength: 1,
                      maxLength: 50,
                      'data-testid': 'test-contactTitle',
                    }}
                    className={classes.formField}
                    {...formState.props('contactTitle')}
                  />
                </Grid>
                <Grid item xs={12}>
                  <EmailTextField
                    margin="none"
                    name={'contactEmail'}
                    label={t('CONTACT_INFO_EMAIL_ADDRESS')}
                    data-testid={'contactEmail'}
                    {...formState.props('contactEmail')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <PhoneTextField
                    name={'contactPhoneNumber'}
                    label={t('CONTACT_INFO_PHONE_NUMBER')}
                    className={classes.formField}
                    inputProps={{ minLength: 1, maxLength: 15 }}
                    data-testid="contactPhoneNumber"
                    {...formState.props('contactPhoneNumber')}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Paper>
      </Grid>
    </>
  );
};

export const createDirectAccessHistoricalUsageFormAdapter = () => {
  return {
    toFormState: (
      initial: DirectAccessHistoricalUsageModel,
    ): DirectAccessHistoricalUsageFormState => {
      return {
        businesses: initial.businesses,
        authType: initial.authType,
        isSamePersonAuthorizing: initial.isSamePersonAuthorizing ?? true,
        companyName: initial.contactInfo?.companyName ?? '',
        contactFullName: initial.contactInfo?.fullName ?? '',
        contactTitle: initial.contactInfo?.title ?? '',
        contactEmail: initial.contactInfo?.emailAddress ?? '',
        contactPhoneNumber: initial.contactInfo?.phoneNumber ?? '',
        authFullName: initial.authInfo?.fullName ?? '',
        authTitle: initial.authInfo?.title ?? '',
        authEmail: initial.authInfo?.emailAddress ?? '',
        authPhoneNumber: initial.authInfo?.phoneNumber ?? '',
        acknowledgements: initial.acknowledgements ?? [],
      };
    },

    fromFormState: (
      state: DirectAccessHistoricalUsageFormState,
    ): DirectAccessHistoricalUsageModel => {
      return {
        businesses: state.businesses,
        authType: state.authType,
        isSamePersonAuthorizing: state.isSamePersonAuthorizing,
        contactInfo: {
          companyName: state.companyName,
          fullName: state.contactFullName,
          title: state.contactTitle,
          emailAddress: state.contactEmail,
          phoneNumber: state.contactPhoneNumber,
        },
        authInfo: {
          fullName: state.isSamePersonAuthorizing
            ? state.contactFullName
            : state.authFullName,
          title: state.isSamePersonAuthorizing
            ? state.contactTitle
            : state.authTitle,
          emailAddress: state.isSamePersonAuthorizing
            ? state.contactEmail
            : state.authEmail,
          phoneNumber: state.isSamePersonAuthorizing
            ? state.contactPhoneNumber
            : state.authPhoneNumber,
        },
        acknowledgements: state.acknowledgements,
      };
    },
  };
};

export const createDirectAccessValidateFunction = () => {
  const { t } = useTranslation();
  return convertValidationRules<DirectAccessHistoricalUsageFormState>(
    (context: BaseValidationContext<DirectAccessHistoricalUsageFormState>) => {
      const {
        values: { isSamePersonAuthorizing },
      } = context;
      return {
        companyName: validateNameField.bind(null, 'Company Name'),
        contactFullName: validateExistsAndAlphabetCharacters.bind(
          null,
          'Full Name',
        ),
        contactTitle: validateExistsAndAlphabetCharacters.bind(null, 'Title'),
        contactEmail: validateEmail,
        contactPhoneNumber: validatePhoneNumber,
        isSamePersonAuthorizing: () => null,
        authFullName: isSamePersonAuthorizing
          ? () => null
          : validateExistsAndAlphabetCharacters.bind(null, 'Full Name'),
        authTitle: isSamePersonAuthorizing
          ? () => null
          : validateExistsAndAlphabetCharacters.bind(null, 'Title'),
        authEmail: isSamePersonAuthorizing ? () => null : validateEmail,
        authPhoneNumber: isSamePersonAuthorizing
          ? () => null
          : validatePhoneNumber,
      };
    },
  );
};
